import { useState } from 'react'
import PageContainer from '@/common/components/PageContainer'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Skeleton } from '@/common/ui/skeleton'
import { Button } from '@/common/ui/button'
import { Progress } from '@/common/ui/progress'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import {
	RefreshCw,
	Cpu,
	HardDrive,
	Activity,
	Database,
	TrendingUp,
	BarChart3,
} from 'lucide-react'
import useQueryGetEcsServiceCpuMem from '../../api/hooks/useQueryGetEcsServiceCpuMem'
import { toast } from 'sonner'
import MetricsChart from '../../components/MetricsChart'
import TaskMetricsTable from '../../components/TaskMetricsTable'

function SystemMetricsPage() {
	const [minutes, setMinutes] = useState(60)
	const [periodSeconds, setPeriodSeconds] = useState(60)

	const { data, isLoading, refetch, isRefetching } =
		useQueryGetEcsServiceCpuMem({
			minutes,
			periodSeconds,
		})

	const handleRefresh = () => {
		refetch()
		toast.success('อัปเดตข้อมูลเรียบร้อยแล้ว')
	}

	const formatPercentage = (value: number | null): string => {
		if (value === null) return 'N/A'
		return `${value.toFixed(2)}%`
	}

	const getStatusColor = (value: number | null): string => {
		if (value === null) return 'text-muted-foreground'
		if (value < 50) return 'text-green-600 dark:text-green-400'
		if (value < 80) return 'text-yellow-600 dark:text-yellow-400'
		return 'text-red-600 dark:text-red-400'
	}

	return (
		<PageContainer>
			<div className="space-y-8">
				{/* Header */}
				<div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
					<div className="space-y-2">
						<div className="flex items-center gap-3">
							<div>
								<h1 className="bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-4xl font-bold tracking-tight text-transparent">
									ตัวชี้วัดระบบ
								</h1>
								<p className="mt-1 text-muted-foreground">
									ตรวจสอบประสิทธิภาพของระบบ (CPU, Memory, Database CPU, และ
									Tasks)
								</p>
							</div>
						</div>
					</div>
					<div className="flex flex-wrap items-center gap-3">
						<div className="flex items-center gap-2 rounded-lg border bg-card p-2">
							<label className="whitespace-nowrap text-sm font-medium">
								ช่วงเวลา:
							</label>
							<Select
								value={minutes.toString()}
								onValueChange={(value) => setMinutes(Number(value))}
							>
								<SelectTrigger className="w-[140px] border-0 shadow-none">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="15">15 นาที</SelectItem>
									<SelectItem value="30">30 นาที</SelectItem>
									<SelectItem value="60">1 ชั่วโมง</SelectItem>
									<SelectItem value="120">2 ชั่วโมง</SelectItem>
									<SelectItem value="240">4 ชั่วโมง</SelectItem>
									<SelectItem value="480">8 ชั่วโมง</SelectItem>
									<SelectItem value="1440">24 ชั่วโมง</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="flex items-center gap-2 rounded-lg border bg-card p-2">
							<label className="whitespace-nowrap text-sm font-medium">
								ความละเอียด:
							</label>
							<Select
								value={periodSeconds.toString()}
								onValueChange={(value) => setPeriodSeconds(Number(value))}
							>
								<SelectTrigger className="w-[140px] border-0 shadow-none">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="60">1 นาที</SelectItem>
									<SelectItem value="300">5 นาที</SelectItem>
									<SelectItem value="600">10 นาที</SelectItem>
									<SelectItem value="1800">30 นาที</SelectItem>
									<SelectItem value="3600">1 ชั่วโมง</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<Button
							variant="default"
							size="sm"
							onClick={handleRefresh}
							disabled={isRefetching}
							className="shadow-md transition-all hover:shadow-lg"
						>
							<RefreshCw
								className={`mr-2 h-4 w-4 ${isRefetching ? 'animate-spin' : ''}`}
							/>
							อัปเดต
						</Button>
					</div>
				</div>

				{/* Metrics Cards */}
				{isLoading ? (
					<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
						{[...Array(6)].map((_, i) => (
							<Card key={i} className="overflow-hidden">
								<CardHeader>
									<Skeleton className="h-6 w-32" />
								</CardHeader>
								<CardContent>
									<Skeleton className="h-12 w-full" />
								</CardContent>
							</Card>
						))}
					</div>
				) : (
					<>
						{/* CPU Metrics */}
						<div className="space-y-6">
							<div className="flex items-center gap-3">
								<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 shadow-md">
									<Cpu className="h-5 w-5 text-white" />
								</div>
								<h2 className="text-2xl font-bold tracking-tight">
									CPU Utilization
								</h2>
							</div>
							<div className="grid gap-6 md:grid-cols-2">
								<Card className="group relative overflow-hidden border-2 transition-all duration-300 hover:border-blue-500/50 hover:shadow-xl">
									<div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
									<CardHeader className="relative bg-gradient-to-r from-blue-50 to-blue-100/50 pb-4 dark:from-blue-950/50 dark:to-blue-900/30">
										<div className="flex items-center justify-between">
											<CardTitle className="text-sm font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
												ค่าเฉลี่ย CPU
											</CardTitle>
											<div className="rounded-full bg-blue-500/10 p-2">
												<Cpu className="h-5 w-5 text-blue-600 dark:text-blue-400" />
											</div>
										</div>
									</CardHeader>
									<CardContent className="relative pt-6">
										<div className="space-y-4">
											<div>
												<p
													className={`text-5xl font-bold transition-colors ${getStatusColor(data?.cpu.average ?? null)}`}
												>
													{formatPercentage(data?.cpu.average ?? null)}
												</p>
												{data?.cpu.average !== null &&
													data?.cpu.average !== undefined && (
														<Progress
															value={data.cpu.average}
															className="mt-4 h-2"
														/>
													)}
											</div>
											<div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm">
												<TrendingUp className="h-4 w-4 text-muted-foreground" />
												<span className="text-muted-foreground">
													จำนวนข้อมูล:
												</span>
												<span className="font-semibold">
													{data?.cpu.datapoints ?? 0} จุด
												</span>
											</div>
										</div>
									</CardContent>
								</Card>

								<Card className="group relative overflow-hidden border-2 transition-all duration-300 hover:border-orange-500/50 hover:shadow-xl">
									<div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
									<CardHeader className="relative bg-gradient-to-r from-orange-50 to-orange-100/50 pb-4 dark:from-orange-950/50 dark:to-orange-900/30">
										<div className="flex items-center justify-between">
											<CardTitle className="text-sm font-semibold uppercase tracking-wide text-orange-700 dark:text-orange-300">
												ค่าสูงสุด CPU
											</CardTitle>
											<div className="rounded-full bg-orange-500/10 p-2">
												<Activity className="h-5 w-5 text-orange-600 dark:text-orange-400" />
											</div>
										</div>
									</CardHeader>
									<CardContent className="relative pt-6">
										<div className="space-y-4">
											<div>
												<p
													className={`text-5xl font-bold transition-colors ${getStatusColor(data?.cpu.max ?? null)}`}
												>
													{formatPercentage(data?.cpu.max ?? null)}
												</p>
												{data?.cpu.max !== null &&
													data?.cpu.max !== undefined && (
														<Progress
															value={data.cpu.max}
															className="mt-4 h-2"
														/>
													)}
											</div>
											<div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm">
												<TrendingUp className="h-4 w-4 text-muted-foreground" />
												<span className="text-muted-foreground">
													จำนวนข้อมูล:
												</span>
												<span className="font-semibold">
													{data?.cpu.datapoints ?? 0} จุด
												</span>
											</div>
										</div>
									</CardContent>
								</Card>
							</div>
						</div>

						{/* Memory Metrics */}
						<div className="space-y-6">
							<div className="flex items-center gap-3">
								<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 shadow-md">
									<HardDrive className="h-5 w-5 text-white" />
								</div>
								<h2 className="text-2xl font-bold tracking-tight">
									Memory Utilization
								</h2>
							</div>
							<div className="grid gap-6 md:grid-cols-2">
								<Card className="group relative overflow-hidden border-2 transition-all duration-300 hover:border-purple-500/50 hover:shadow-xl">
									<div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
									<CardHeader className="relative bg-gradient-to-r from-purple-50 to-purple-100/50 pb-4 dark:from-purple-950/50 dark:to-purple-900/30">
										<div className="flex items-center justify-between">
											<CardTitle className="text-sm font-semibold uppercase tracking-wide text-purple-700 dark:text-purple-300">
												ค่าเฉลี่ย Memory
											</CardTitle>
											<div className="rounded-full bg-purple-500/10 p-2">
												<HardDrive className="h-5 w-5 text-purple-600 dark:text-purple-400" />
											</div>
										</div>
									</CardHeader>
									<CardContent className="relative pt-6">
										<div className="space-y-4">
											<div>
												<p
													className={`text-5xl font-bold transition-colors ${getStatusColor(data?.memory.average ?? null)}`}
												>
													{formatPercentage(data?.memory.average ?? null)}
												</p>
												{data?.memory.average !== null &&
													data?.memory.average !== undefined && (
														<Progress
															value={data.memory.average}
															className="mt-4 h-2"
														/>
													)}
											</div>
											<div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm">
												<TrendingUp className="h-4 w-4 text-muted-foreground" />
												<span className="text-muted-foreground">
													จำนวนข้อมูล:
												</span>
												<span className="font-semibold">
													{data?.memory.datapoints ?? 0} จุด
												</span>
											</div>
										</div>
									</CardContent>
								</Card>

								<Card className="group relative overflow-hidden border-2 transition-all duration-300 hover:border-pink-500/50 hover:shadow-xl">
									<div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
									<CardHeader className="relative bg-gradient-to-r from-pink-50 to-pink-100/50 pb-4 dark:from-pink-950/50 dark:to-pink-900/30">
										<div className="flex items-center justify-between">
											<CardTitle className="text-sm font-semibold uppercase tracking-wide text-pink-700 dark:text-pink-300">
												ค่าสูงสุด Memory
											</CardTitle>
											<div className="rounded-full bg-pink-500/10 p-2">
												<Activity className="h-5 w-5 text-pink-600 dark:text-pink-400" />
											</div>
										</div>
									</CardHeader>
									<CardContent className="relative pt-6">
										<div className="space-y-4">
											<div>
												<p
													className={`text-5xl font-bold transition-colors ${getStatusColor(data?.memory.max ?? null)}`}
												>
													{formatPercentage(data?.memory.max ?? null)}
												</p>
												{data?.memory.max !== null &&
													data?.memory.max !== undefined && (
														<Progress
															value={data.memory.max}
															className="mt-4 h-2"
														/>
													)}
											</div>
											<div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm">
												<TrendingUp className="h-4 w-4 text-muted-foreground" />
												<span className="text-muted-foreground">
													จำนวนข้อมูล:
												</span>
												<span className="font-semibold">
													{data?.memory.datapoints ?? 0} จุด
												</span>
											</div>
										</div>
									</CardContent>
								</Card>
							</div>
						</div>

						{/* Database CPU Metrics */}
						<div className="space-y-6">
							<div className="flex items-center gap-3">
								<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-green-500 to-green-600 shadow-md">
									<Database className="h-5 w-5 text-white" />
								</div>
								<h2 className="text-2xl font-bold tracking-tight">
									Database CPU Utilization
								</h2>
							</div>
							<div className="grid gap-6 md:grid-cols-2">
								<Card className="group relative overflow-hidden border-2 transition-all duration-300 hover:border-green-500/50 hover:shadow-xl">
									<div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
									<CardHeader className="relative bg-gradient-to-r from-green-50 to-green-100/50 pb-4 dark:from-green-950/50 dark:to-green-900/30">
										<div className="flex items-center justify-between">
											<CardTitle className="text-sm font-semibold uppercase tracking-wide text-green-700 dark:text-green-300">
												ค่าเฉลี่ย Database CPU
											</CardTitle>
											<div className="rounded-full bg-green-500/10 p-2">
												<Database className="h-5 w-5 text-green-600 dark:text-green-400" />
											</div>
										</div>
									</CardHeader>
									<CardContent className="relative pt-6">
										<div className="space-y-4">
											<div>
												<p
													className={`text-5xl font-bold transition-colors ${getStatusColor(data?.databaseCpu.average ?? null)}`}
												>
													{formatPercentage(data?.databaseCpu.average ?? null)}
												</p>
												{data?.databaseCpu.average !== null &&
													data?.databaseCpu.average !== undefined && (
														<Progress
															value={data.databaseCpu.average}
															className="mt-4 h-2"
														/>
													)}
											</div>
											<div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm">
												<TrendingUp className="h-4 w-4 text-muted-foreground" />
												<span className="text-muted-foreground">
													จำนวนข้อมูล:
												</span>
												<span className="font-semibold">
													{data?.databaseCpu.datapoints ?? 0} จุด
												</span>
											</div>
										</div>
									</CardContent>
								</Card>

								<Card className="group relative overflow-hidden border-2 transition-all duration-300 hover:border-teal-500/50 hover:shadow-xl">
									<div className="absolute inset-0 bg-gradient-to-br from-teal-500/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
									<CardHeader className="relative bg-gradient-to-r from-teal-50 to-teal-100/50 pb-4 dark:from-teal-950/50 dark:to-teal-900/30">
										<div className="flex items-center justify-between">
											<CardTitle className="text-sm font-semibold uppercase tracking-wide text-teal-700 dark:text-teal-300">
												ค่าสูงสุด Database CPU
											</CardTitle>
											<div className="rounded-full bg-teal-500/10 p-2">
												<Activity className="h-5 w-5 text-teal-600 dark:text-teal-400" />
											</div>
										</div>
									</CardHeader>
									<CardContent className="relative pt-6">
										<div className="space-y-4">
											<div>
												<p
													className={`text-5xl font-bold transition-colors ${getStatusColor(data?.databaseCpu.max ?? null)}`}
												>
													{formatPercentage(data?.databaseCpu.max ?? null)}
												</p>
												{data?.databaseCpu.max !== null &&
													data?.databaseCpu.max !== undefined && (
														<Progress
															value={data.databaseCpu.max}
															className="mt-4 h-2"
														/>
													)}
											</div>
											<div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm">
												<TrendingUp className="h-4 w-4 text-muted-foreground" />
												<span className="text-muted-foreground">
													จำนวนข้อมูล:
												</span>
												<span className="font-semibold">
													{data?.databaseCpu.datapoints ?? 0} จุด
												</span>
											</div>
										</div>
									</CardContent>
								</Card>
							</div>
						</div>

						{/* Per-Task Metrics Table */}
						<TaskMetricsTable
							tasks={data?.taskMetrics ?? []}
							isLoading={isLoading}
						/>

						{/* CPU, Memory, and Database CPU Usage Graphs - Three columns */}
						<div className="space-y-6">
							<div className="flex items-center gap-3">
								<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 shadow-md">
									<BarChart3 className="h-5 w-5 text-white" />
								</div>
								<h2 className="text-2xl font-bold tracking-tight">
									Time Series Charts
								</h2>
							</div>
							<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
								<MetricsChart
									data={data?.cpu.timeSeries ?? []}
									title="CPU Utilization Over Time"
									description="แสดงการใช้งาน CPU ตามช่วงเวลา"
									color="#3b82f6"
									isLoading={isLoading}
								/>
								<MetricsChart
									data={data?.memory.timeSeries ?? []}
									title="Memory Utilization Over Time"
									description="แสดงการใช้งาน Memory ตามช่วงเวลา"
									color="#8b5cf6"
									isLoading={isLoading}
								/>
								<MetricsChart
									data={data?.databaseCpu.timeSeries ?? []}
									title="Database CPU Utilization Over Time"
									description="แสดงการใช้งาน Database CPU ตามช่วงเวลา"
									color="#10b981"
									isLoading={isLoading}
								/>
							</div>
						</div>
					</>
				)}
			</div>
		</PageContainer>
	)
}

export default SystemMetricsPage
